import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  ButtonExportCSVAsync,
  ButtonText,
  CapsModal,
  Card,
  CardContent,
  CardDetail,
  CardIconStatus,
  CardLabel,
  CardRow,
  CardValue,
  HeaderCell,
  HeaderRow,
  ModalFactory,
  PageHeader,
  PageHeaderContent,
  PageTitle,
  PaymentOverviewStats,
  PaymentStatusPills,
  Section,
  SectionHeader,
  SortLabel,
  Table,
  TableBody,
  TableHead,
  Text,
  UnallocatedFundsModal,
} from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { PayoutDetails } from 'modals';
import { URL } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { getPaymentRequest, Payment, selectPayment, selectPaymentPayoutStats } from 'store/Payment';
import { selectSiteSettings } from 'store/Site';
import { IStore } from 'store/types';
import { ApiQueryOperator, IPaymentSet, IPayout, ITipCalculatorResultWarning, ITipCalculatorResultWarningUnallocatedRemainder, PayoutRefType, TipCalculatorRuleConfiguration, TipCalculatorWarningType } from 'types';
import { buildTipCalculatorWarnings, centsToDollarsDisplay, formatDateForApi } from 'utils';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import * as API from '../../api';
import CalculationsReport from './components/CalculationsReport';
import ChangeLog from './components/ChangeLog';
import CollectedTips from './components/CollectedTips';
import PoolReport from './components/PoolReport';
import ReceivedTips from './components/ReceivedTips';
import SettingsReport from './components/SettingsReport';
import { IExportData } from './components/types';
import { PayeeRow, PaymentDescription } from './styles';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(advancedFormat);

const StyledSection = styled(Section)`
  padding: 0 ${toRem(theme.layout.gutter.small)} ${toRem(44)} ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)} ${toRem(44)} ${toRem(theme.layout.gutter.large)};
  }
`;

const StyledSectionHeader = styled(SectionHeader)`
  flex-flow: row wrap-reverse;
  width: 100%;

  @media (max-width: ${theme.breakpoint.med}px) {
    justify-content: center;

    h3,
    div {
      width: 100%;
    }
  }
`;

const CalculatorReportNavWrapper = styled.div`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: flex-start;
  align-items: center;
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.xsmall}px;
  padding: ${toRem(4)};
  flex-flow: column nowrap;
  width: auto;
  margin-top: ${toRem(8)};

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row nowrap;
    width: 100%;
    margin-bottom: 0;
  }
`;

const CalculatorReportNavItem = styled.button.attrs({
  type: 'button',
})`
  padding: ${toRem(8)} ${toRem(16)};
  font-size: ${toRem(11)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[600]};
  border-radius: ${theme.borderRadius.xsmall}px;
  width: 100%;
  margin-bottom: ${toRem(4)};

  &.active {
    background-color: ${theme.color.secondary[400]};
    color: #fff;

    &:hover {
      cursor: auto;
      background-color: ${theme.color.secondary[400]};
    }
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    width: auto;
    text-align: center;
    margin: ${toRem(0)} ${toRem(2)};
    padding: ${toRem(4)} ${toRem(16)};

    &:hover {
      cursor: pointer;
      background-color: ${theme.color.neutral[200]};
    }
  }
`;

const Warning = styled.div`
  display: flex;
  padding: ${toRem(8)} ${toRem(22)};
  justify-content: center;
  align-items: center;
  gap: ${toRem(8)};
  border-radius: ${toRem(6)};
  background-color: ${theme.color.warning.light};

  p {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
  }

  button {
    color: ${theme.color.primary[500]};
    font-weight: ${theme.font.weight.medium};
    text-align: left;
    text-decoration: none;

    &:hover {
      cursor: pointer;
      color: ${theme.color.primary[600]};
    }
  }
`;

enum View {
  SUMMARY = 'summary',
  CALCULATIONS = 'calculations',
  COLLECTED_TIPS = 'collected_tips',
  POOL_REPORT = 'pool_report',
  RECEIVED_TIPS = 'received_tips',
  SETTINGS_REPORT = 'settings_report',
  CHANGE_LOG = 'change_log',
}

const PaymentSet = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [view, setView] = useState<View>(View.SUMMARY);
  const [sortOption, setSortOption] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'employee.name', direction: 'asc' });
  const [currentModalPayout, setCurrentModalPayout] = useState<IPayout | null>(null);

  const payment = useSelector(selectPayment());
  const payoutStats = useSelector(selectPaymentPayoutStats());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payment.GET_PAYMENT_REQUEST));
  const currentEmployee = useSelector(selectCurrentEmployee());
  const { tipCalculatorRuleConfiguration } = useSelector(selectSiteSettings()) || {};
  const isAdvanced = tipCalculatorRuleConfiguration === TipCalculatorRuleConfiguration.ADVANCED;
  const payouts = payment?.payouts;

  // Get Tip Calculator Warnings
  const tipCalculatorWarnings = useMemo(() => buildTipCalculatorWarnings(payment?.calculations?.results || []), [payment]);

  // Filter for cap warnings
  const cappedWarnings = useMemo(() => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.CAPPED_REMAINDER, TipCalculatorWarningType.CAPPED_SHARE].includes(warning.type)), [tipCalculatorWarnings]);

  // Filter for unallocated funds warnings
  const unallocatedFundsWarnings = useMemo(
    () => tipCalculatorWarnings.filter((warning) => [TipCalculatorWarningType.UNALLOCATED_REMAINDER].includes(warning.type)) as ITipCalculatorResultWarningUnallocatedRemainder[],
    [tipCalculatorWarnings]
  );

  const getPayment = useCallback(() => {
    dispatch(getPaymentRequest(id));
  }, [id]);

  useEffect(() => {
    getPayment();
  }, [dispatch, id]);

  useEffect(() => {
    if (payment?.site.id) dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: payment.site.id }));
  }, [dispatch, payment]);

  const sortedPayouts = orderBy(payouts, [sortOption.field], [sortOption.direction]);

  const directionForField = (field: string) => {
    if (sortOption.field === field && sortOption.direction === 'asc') return 'desc';
    return 'asc';
  };

  // Modal
  const [showModal, hideModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideModal} isActive>
        <PayoutDetails payoutSite={payment?.site} payout={currentModalPayout} onClose={hideModal} onUpdate={getPayment} />
      </ModalFactory>
    ),
    [currentModalPayout, getPayment]
  );

  const [showCapWarningModal, hideCapWarningdModal] = useModal(() => <CapsModal warnings={cappedWarnings} hideModal={hideCapWarningdModal} />, [payment, cappedWarnings]);

  const [showUnallocatedFundsWarningModal, hideUnallocatedFundsWarningdModal] = useModal(
    () => <UnallocatedFundsModal warnings={unallocatedFundsWarnings} tipCalcResults={payment?.calculations?.results} hideModal={hideUnallocatedFundsWarningdModal} />,
    [payment, unallocatedFundsWarnings]
  );

  const exportPayoutsCSV = async (payment: IPaymentSet): Promise<IExportCSV> => {
    const response = await API.DataExport.sitePayouts(payment.site.id, { q: [{ key: 'paymentId', operator: ApiQueryOperator.eq, value: payment.id }], offset: 0, limit: 0 });
    return CSVFormatter.payouts(response.body);
  };

  const calculatorReportNav = (payment: IPaymentSet) => (
    <CalculatorReportNavWrapper data-cy="tip-calculation-report-nav">
      <CalculatorReportNavItem onClick={() => setView(View.SUMMARY)} {...(view === View.SUMMARY && { className: 'active' })} data-cy="tip-calculation-report-nav-item-summary">
        Summary
      </CalculatorReportNavItem>
      <CalculatorReportNavItem onClick={() => setView(View.CALCULATIONS)} {...(view === View.CALCULATIONS && { className: 'active' })} data-cy="tip-calculation-report-nav-item-calculations">
        Calculations
      </CalculatorReportNavItem>
      <CalculatorReportNavItem onClick={() => setView(View.COLLECTED_TIPS)} {...(view === View.COLLECTED_TIPS && { className: 'active' })} data-cy="tip-calculation-report-nav-item-collected-tips">
        Collected Tips
      </CalculatorReportNavItem>
      <CalculatorReportNavItem onClick={() => setView(View.POOL_REPORT)} {...(view === View.POOL_REPORT && { className: 'active' })} data-cy="tip-calculation-report-nav-item-pool-report">
        Pool Report
      </CalculatorReportNavItem>
      <CalculatorReportNavItem onClick={() => setView(View.RECEIVED_TIPS)} {...(view === View.RECEIVED_TIPS && { className: 'active' })} data-cy="tip-calculation-report-nav-item-received-tips">
        Received Tips
      </CalculatorReportNavItem>
      <CalculatorReportNavItem onClick={() => setView(View.SETTINGS_REPORT)} {...(view === View.SETTINGS_REPORT && { className: 'active' })} data-cy="tip-calculation-report-nav-item-settings">
        Settings
      </CalculatorReportNavItem>
      {payment?.changeLog?.length > 0 && (
        <CalculatorReportNavItem onClick={() => setView(View.CHANGE_LOG)} {...(view === View.CHANGE_LOG && { className: 'active' })} data-cy="tip-calculation-report-nav-item-change-log">
          Change Log
        </CalculatorReportNavItem>
      )}
    </CalculatorReportNavWrapper>
  );

  const header = (data?: IExportData, cappedWarnings?: ITipCalculatorResultWarning[], unallocatedFundsWarnings?: ITipCalculatorResultWarningUnallocatedRemainder[]) => (
    <StyledSectionHeader isLoading={isLoading} {...(payment?.refType !== PayoutRefType.CALCULATOR ? { title: 'Payees', value: `(${payouts?.length || '--'})` } : { actions: calculatorReportNav(payment) })} dataCy="payment-set-header">
      {data?.exportFunc && (
        <ButtonExportCSVAsync
          disabled={!payment || isLoading}
          exportFunc={data.exportFunc}
          fileName={`PaymentSet_${payment?.site.name}_${payment?.id}_${data?.fileName ? `${data.fileName}_` : ''}${formatDateForApi(payment?.paymentDate)}`}
        />
      )}
      {cappedWarnings?.length > 0 && (
        <Warning data-cy="cap-warning">
          <Text size="small">Tip Share Amounts Capped.</Text>
          <ButtonText label="View Details" onClick={showCapWarningModal} fontSize={14} />
        </Warning>
      )}
      {unallocatedFundsWarnings?.length > 0 && (
        <Warning data-cy="unallocated-funds-warning">
          <Text size="small">Unallocated Tips {centsToDollarsDisplay(unallocatedFundsWarnings.reduce((sum, warning) => sum + (warning.remainder?.unallocated?.total ?? 0), 0))}.</Text>
          <ButtonText label="View Details" onClick={showUnallocatedFundsWarningModal} fontSize={14} />
        </Warning>
      )}
    </StyledSectionHeader>
  );

  return (
    <>
      <PageHeader hasStats>
        <Breadcrumbs>
          <BreadcrumbsItem label="Payments" to={URL.PaymentsOverview} />
          <BreadcrumbsItem label="Payment Set" />
        </Breadcrumbs>

        <PageHeaderContent>
          <PageTitle data-cy="page-title" title={id} subtitle={`Total: ${`${(payment?.totals && centsToDollarsDisplay(payment?.totals.net)) || '--'}`}`} />
        </PageHeaderContent>

        <CardRow>
          <Card>
            <CardIconStatus color={Appearance.NEUTRAL} icon="Site" isLoading={isLoading} />
            <CardContent data-cy="site-card">
              <CardLabel isLoading={isLoading}>Site</CardLabel>
              <CardValue isLoading={isLoading}>{payment?.site.name || '--'}</CardValue>
            </CardContent>
          </Card>

          <Card>
            <CardIconStatus color={Appearance.NEUTRAL} icon="Person" isLoading={isLoading} />
            <CardContent data-cy="sent-by-card">
              <CardLabel isLoading={isLoading}>Sent By</CardLabel>
              <CardValue isLoading={isLoading}>{payment?.createdBy.name || '--'}</CardValue>
            </CardContent>
          </Card>

          <Card>
            <CardIconStatus color={Appearance.NEUTRAL} icon="Calendar" isLoading={isLoading} />
            <CardContent data-cy="submitted-date-card">
              <CardLabel isLoading={isLoading}>Submitted</CardLabel>
              <CardValue isLoading={isLoading}>{payment?.createdAt ? dayjs(payment.createdAt).local().format('MM-DD-YYYY') : '--'}</CardValue>
              <CardDetail isLoading={isLoading}>{payment?.createdAt ? dayjs(payment.createdAt).local().format('h:mm A z') : '--'}</CardDetail>
            </CardContent>
          </Card>

          <Card>
            <CardIconStatus color={Appearance.NEUTRAL} icon="Calendar" isLoading={isLoading} />
            <CardContent data-cy="business-date-card">
              <CardLabel isLoading={isLoading}>Business Date</CardLabel>
              <CardValue isLoading={isLoading}>{payment?.paymentDate ? formatDateForApi(payment.paymentDate) : '--'}</CardValue>
            </CardContent>
          </Card>
        </CardRow>
      </PageHeader>

      <StyledSection afterHeaderCardRow>
        <PaymentDescription data-cy="payment-description" description={payment?.description || 'No description provided'} isLoading={isLoading} />
      </StyledSection>

      <Section>
        {view === View.SUMMARY && (
          <>
            {header({ exportFunc: () => exportPayoutsCSV(payment) })}
            <PaymentStatusPills payoutStats={payoutStats} isLoading={isLoading} />
            <PaymentOverviewStats payoutStats={payoutStats} isLoading={isLoading} />
            <Table scrollable sticky>
              <TableHead>
                <HeaderRow sticky>
                  <HeaderCell>
                    <SortLabel
                      label="Payee"
                      isSorted={sortOption.field === 'employee.name'}
                      data-cy="payee-column-label"
                      isSortedDesc={sortOption.direction === 'desc'}
                      onClick={() =>
                        setSortOption({
                          field: 'employee.name',
                          direction: directionForField('employee.name'),
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell>
                    <SortLabel
                      label="Status"
                      isSorted={sortOption.field === 'status'}
                      isSortedDesc={sortOption.direction === 'desc'}
                      data-cy="status-column-label"
                      onClick={() =>
                        setSortOption({
                          field: 'status',
                          direction: directionForField('status'),
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell data-cy="tips-column-label">Tips</HeaderCell>

                  <HeaderCell align="left">
                    <SortLabel
                      label="Autograts"
                      isSorted={sortOption.field === 'autoGratuity'}
                      data-cy="autograts-column-label"
                      isSortedDesc={sortOption.direction === 'desc'}
                      onClick={() =>
                        setSortOption({
                          field: 'autoGratuity',
                          direction: directionForField('autoGratuity'),
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell align="left">
                    <SortLabel
                      label="Gross"
                      isSorted={sortOption.field === 'gross'}
                      data-cy="gross-column-label"
                      isSortedDesc={sortOption.direction === 'desc'}
                      onClick={() =>
                        setSortOption({
                          field: 'gross',
                          direction: directionForField('gross'),
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell align="left">
                    <SortLabel
                      label="Holdback"
                      data-cy="holdback-column-label"
                      isSorted={sortOption.field === 'holdback'}
                      isSortedDesc={sortOption.direction === 'desc'}
                      onClick={() =>
                        setSortOption({
                          field: 'holdback',
                          direction: directionForField('holdback'),
                        })
                      }
                    />
                  </HeaderCell>

                  <HeaderCell align="left">
                    <SortLabel
                      label="Net"
                      isSorted={sortOption.field === 'net'}
                      isSortedDesc={sortOption.direction === 'desc'}
                      data-cy="paid-column-label"
                      onClick={() =>
                        setSortOption({
                          field: 'net',
                          direction: directionForField('net'),
                        })
                      }
                    />
                  </HeaderCell>
                </HeaderRow>
              </TableHead>

              <TableBody>
                {sortedPayouts?.map((payout: IPayout, index: number) => (
                  <PayeeRow
                    data-cy="payout-row"
                    key={`payout-${index}`}
                    payout={payout}
                    isLoading={isLoading}
                    siteUsesHoldback
                    onClick={() => {
                      setCurrentModalPayout(payout);
                      showModal();
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {view === View.CALCULATIONS && <CalculationsReport header={header} calculations={payment?.calculations} payouts={payouts} isLoading={isLoading} />}
        {view === View.COLLECTED_TIPS && <CollectedTips header={header} calculations={payment?.calculations} isLoading={isLoading} />}
        {view === View.POOL_REPORT && <PoolReport header={header} calculations={payment?.calculations} businessDay={payment?.paymentDate} isLoading={isLoading} />}
        {view === View.RECEIVED_TIPS && <ReceivedTips header={header} calculations={payment?.calculations} isLoading={isLoading} />}
        {view === View.SETTINGS_REPORT && <SettingsReport header={header} calculations={payment?.calculations} businessDay={payment?.paymentDate} isAdvanced={isAdvanced} isLoading={isLoading} />}
        {view === View.CHANGE_LOG && <ChangeLog header={header} changeLogEntries={payment?.changeLog} isLoading={isLoading} />}
      </Section>
    </>
  );
};

export default PaymentSet;
